/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Blend Compositing Node
   Multi-layer image blending with standard blend formulas and
   explicit straight-alpha compositing rules.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER, GLSL_BLEND_MODES } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';
import { getTextureFromMedia } from '../../rendering/media';

const BLEND_SHADER = `${GLSL_HEADER}
${GLSL_BLEND_MODES}

uniform sampler2D u_base;
uniform sampler2D u_blend;
uniform bool u_hasBlend;
uniform int u_mode;
uniform float u_opacity; // 0.0 .. 1.0

void main() {
  vec4 baseCol = texture(u_base, v_uv);
  
  if (!u_hasBlend) {
    fragColor = baseCol;
    return;
  }
  
  vec4 blendCol = texture(u_blend, v_uv);
  
  vec3 blended = baseCol.rgb;
  if (u_mode == 0) blended = blendNormal(baseCol.rgb, blendCol.rgb);
  else if (u_mode == 1) blended = blendMultiply(baseCol.rgb, blendCol.rgb);
  else if (u_mode == 2) blended = blendScreen(baseCol.rgb, blendCol.rgb);
  else if (u_mode == 3) blended = blendOverlay(baseCol.rgb, blendCol.rgb);
  else if (u_mode == 4) blended = blendDarken(baseCol.rgb, blendCol.rgb);
  else if (u_mode == 5) blended = blendLighten(baseCol.rgb, blendCol.rgb);
  else if (u_mode == 6) blended = blendDifference(baseCol.rgb, blendCol.rgb);

  // Alpha composite: blend layer alpha scaled by user opacity
  float effectiveBlendAlpha = blendCol.a * u_opacity;
  
  // Straight alpha color interpolation
  vec3 finalRgb = mix(baseCol.rgb, blended, effectiveBlendAlpha);
  
  // Standard over-compositing alpha
  float finalAlpha = clamp(baseCol.a + effectiveBlendAlpha * (1.0 - baseCol.a), 0.0, 1.0);
  
  fragColor = vec4(finalRgb, finalAlpha);
}
`;

export const blendNode: NodeDefinition = {
  type: 'composite.blend',
  category: 'Composite',
  label: 'Blend',
  description: 'Composites two image layers with professional blending modes and alpha blending',
  keywords: ['blend', 'composite', 'layer', 'multiply', 'screen', 'overlay', 'mix', 'opacity'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'base', name: 'Base Layer', type: 'image' },
    { id: 'blend', name: 'Blend Layer', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Composited', type: 'image' },
  ],
  params: [
    {
      id: 'mode',
      name: 'Blend Mode',
      type: 'select',
      defaultValue: 'normal',
      options: [
        { label: 'Normal', value: 'normal' },
        { label: 'Multiply', value: 'multiply' },
        { label: 'Screen', value: 'screen' },
        { label: 'Overlay', value: 'overlay' },
        { label: 'Darken', value: 'darken' },
        { label: 'Lighten', value: 'lighten' },
        { label: 'Difference', value: 'difference' },
      ],
    },
    {
      id: 'opacity',
      name: 'Layer Opacity',
      type: 'slider',
      defaultValue: 100,
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: BLEND_SHADER,
    primaryInputPort: 'base',
    getUniforms: (inputs, params) => {
      const modeStr = String(params.mode ?? 'normal');
      const modeMap: Record<string, number> = {
        normal: 0,
        multiply: 1,
        screen: 2,
        overlay: 3,
        darken: 4,
        lighten: 5,
        difference: 6,
      };

      const blendTex = getTextureFromMedia(inputs.blend);
      const hasBlend = Boolean(blendTex && blendTex.nativeHandle);

      return {
        u_base: inputs.base as unknown as import('../../rendering/types').TextureHandle,
        u_blend: hasBlend ? (blendTex as import('../../rendering/types').TextureHandle) : (inputs.base as unknown as import('../../rendering/types').TextureHandle),
        u_hasBlend: hasBlend,
        u_mode: modeMap[modeStr] ?? 0,
        u_opacity: Number(params.opacity ?? 100) / 100,
      };
    },
  }),
};
